import {
  Button,
  styled,
  Tooltip,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import {
  ChevronLeft,
  ChevronRight,
  HelpCircleIcon,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Zoom steps, as a multiple of the label's printed size. 100% is the label at
 * the size it prints.
 */
export const ZOOM_STEPS = [0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8];

/** What a typed zoom can be, in percent of the printed size. */
export const MIN_ZOOM_PERCENT = 50;
export const MAX_ZOOM_PERCENT = 800;

export const FIT_LABEL_HINT =
  "Shows the whole label scaled to fit this view, so it can look bigger or smaller than it prints. The preview is an image from DYMO Connect, so text can look blurry when you zoom in. Your printed label isn't affected.";
export const ACTUAL_SIZE_HINT =
  "Actual size is close to the printed size, but it depends on your display, so it can look a little smaller or bigger than the label.";

/**
 * DYMO Connect renders a screen resolution preview at 96 pixels per inch, the
 * same as a CSS inch, so that PNG shown at its own pixel size is the label at
 * its printed size, as far as the browser knows. It can't know the display's
 * real pixel density, so on a screen where a CSS inch isn't a real inch (most
 * Macs) Actual size comes out a bit smaller or bigger than the printed label.
 */
const DYMO_SCREEN_PPI = 96;
const CSS_PPI = 96;

const STAGE_PADDING = 16;
// The stage takes the label's shape at the stage's width, between these.
const STAGE_MIN_HEIGHT = 160;
const STAGE_MAX_HEIGHT = 520;
const STAGE_MAX_VIEWPORT = 0.6;
// Until the label's shape is known.
const STAGE_DEFAULT_HEIGHT = 240;

type Zoom = { mode: "fit" | "actual" } | { factor: number };

type Size = { width: number; height: number };

/** A PNG's pixel size, remembered with the image it belongs to. */
type Measured = { src: string; size: Size };

export type LabelPreviewZoomNav = {
  /** Which item is shown, zero based. */
  index: number;
  count: number;
  /** Short name of the item shown, like its evidence number. */
  label: string;
  onChange: (index: number) => void;
};

type LabelPreviewZoomProps = {
  /** What's enlarged, like the template name or the item's label. */
  title: string;
  /** The sharpest render there is so far (printer resolution once it lands). */
  src?: string;
  /** The screen resolution render, which sets the label's printed size. */
  screenSrc?: string;
  alt: string;
  /** Shown in place of the image while there's no `src` (loading, an error). */
  placeholder?: React.ReactNode;
  /** Steps through the items of a bulk run. Left out for the template. */
  nav?: LabelPreviewZoomNav;
  /** Changes when a different label is shown, which scrolls back to the top left. */
  resetKey?: string | number;
  onBack: () => void;
};

const nextStep = (factor: number) => ZOOM_STEPS.find((s) => s > factor + 1e-6);

const previousStep = (factor: number) =>
  [...ZOOM_STEPS].reverse().find((s) => s < factor - 1e-6);

const StyledHint = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border-radius: 50%;
  cursor: help;
  color: ${({ theme }) => theme.palette.text.secondary};

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

const StyledHintText = styled.div`
  max-width: 260px;
  white-space: normal;
`;

/** The (?) after a zoom button's text, saying what that view shows. */
const ZoomHint = ({ message }: { message: string }) => (
  <Tooltip
    content={<StyledHintText>{message}</StyledHintText>}
    side="bottom"
    delayDuration={0}
  >
    <StyledHint role="img" aria-label={message}>
      <HelpCircleIcon size={12} />
    </StyledHint>
  </Tooltip>
);

const sizeOf = (img: HTMLImageElement): Size => ({
  width: img.naturalWidth,
  height: img.naturalHeight,
});

const hasSize = (size?: Size): size is Size =>
  !!size && size.width > 0 && size.height > 0;

/**
 * An enlarged view of one label preview, shown in place of the print form so
 * small text and tall labels can be checked before printing. It opens with the
 * label fit to the stage, and the stage takes the label's shape. The image is
 * DYMO Connect's render at the printer's resolution (the preview box's screen
 * resolution render until that lands), only ever scaled, never rotated, so it
 * keeps the label's print orientation.
 */
export function LabelPreviewZoom({
  title,
  src,
  screenSrc,
  alt,
  placeholder,
  nav,
  resetKey,
  onBack,
}: LabelPreviewZoomProps) {
  const theme = useTheme();
  const stageRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<Zoom>({ mode: "fit" });
  const [shown, setShown] = useState<Measured>();
  const [screen, setScreen] = useState<Measured>();
  // The typed zoom while the percent is being edited.
  const [typedPercent, setTypedPercent] = useState<string>();
  const [stageWidth, setStageWidth] = useState(0);
  const [maxStageHeight, setMaxStageHeight] = useState(STAGE_MAX_HEIGHT);

  useLayoutEffect(() => {
    const measure = () => {
      const el = stageRef.current;
      if (!el) return;
      setStageWidth(el.clientWidth - STAGE_PADDING * 2);
      setMaxStageHeight(
        Math.min(window.innerHeight * STAGE_MAX_VIEWPORT, STAGE_MAX_HEIGHT)
      );
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // The screen resolution render's size, when the printer resolution one is
  // what's shown (a second enlarge finds it cached). The image on screen
  // measures it otherwise.
  useEffect(() => {
    if (!screenSrc || screenSrc === src || screen?.src === screenSrc) return;
    const img = new window.Image();
    img.onload = () => setScreen({ src: screenSrc, size: sizeOf(img) });
    img.src = screenSrc;
  }, [screenSrc, src, screen?.src]);

  // A different label starts at the top left, where its text starts. A
  // sharper render of the same label keeps the scroll.
  useEffect(() => {
    stageRef.current?.scrollTo?.(0, 0);
  }, [resetKey]);

  const natural = shown?.src === src ? shown?.size : undefined;
  const screenNatural = screen?.src === screenSrc ? screen?.size : undefined;
  // The label's shape comes from the screen resolution render, on both axes.
  // Whichever PNG is showing fills that box, so a printer resolution render
  // with different x and y resolutions (a 300x600 dpi LabelWriter) can't
  // stretch it, and the swap to the sharper render can't move the layout.
  const knownShape = hasSize(screenNatural) ? screenNatural : natural;
  // Stepping to the next item keeps the last label's shape until the new
  // render loads (a template is one shape), so the stage doesn't jump. The
  // measured state still holds the last label's size then.
  const shape = knownShape ?? screen?.size ?? shown?.size;
  const measured = hasSize(shape) && stageWidth > 0;

  // A wide label gets a short stage and a tall one a tall stage, so the label
  // fills it at Fit label instead of floating in empty space.
  const stageHeight = measured
    ? Math.min(
        Math.max(
          (stageWidth * shape.height) / shape.width + STAGE_PADDING * 2,
          STAGE_MIN_HEIGHT
        ),
        maxStageHeight
      )
    : STAGE_DEFAULT_HEIGHT;

  // Every scale is CSS pixels per pixel of the screen resolution render.
  const fitScale = measured
    ? Math.min(
        stageWidth / shape.width,
        (stageHeight - STAGE_PADDING * 2) / shape.height
      )
    : 1;
  const actualScale =
    measured && hasSize(screenNatural) ? CSS_PPI / DYMO_SCREEN_PPI : undefined;
  // The percent reads against the printed size. Until that's known it reads
  // against fitting the label, and Actual size waits.
  const baseScale = actualScale ?? fitScale;
  const scale =
    "factor" in zoom
      ? baseScale * zoom.factor
      : zoom.mode === "fit"
      ? fitScale
      : baseScale;
  const factor = scale / baseScale;
  const zoomIn = nextStep(factor);
  const zoomOut = previousStep(factor);

  const box = measured
    ? { width: shape.width * scale, height: shape.height * scale }
    : undefined;

  // A typed zoom snaps into range; an empty one changes nothing.
  const commitTypedPercent = () => {
    const typed = Number.parseInt(typedPercent ?? "", 10);
    setTypedPercent(undefined);
    if (Number.isNaN(typed)) return;
    const percent = Math.min(
      Math.max(typed, MIN_ZOOM_PERCENT),
      MAX_ZOOM_PERCENT
    );
    setZoom({ factor: percent / 100 });
  };

  const percentStyle: React.CSSProperties = {
    minWidth: 44,
    height: 24,
    padding: "0 4px",
    textAlign: "center",
    color: "inherit",
    font: "inherit",
    fontSize: 12,
    background: "none",
    border: "none",
    borderRadius: 4,
    cursor: "text",
  };
  // The label sits on the stage's backdrop with a divider edge, like the
  // shared FileViewer's images, so its real shape shows. An outline keeps the
  // edge out of layout, so 100% stays the printed size to the pixel.
  const labelEdge: React.CSSProperties = {
    display: "block",
    background: "#fff",
    outline: `1px solid ${theme.palette.divider}`,
  };
  const imageStyle: React.CSSProperties = box
    ? {
        ...labelEdge,
        width: box.width,
        height: box.height,
        maxWidth: "none",
        // Smooth past the render's own pixels: soft text reads better than
        // blocky text.
        imageRendering: "auto",
      }
    : {
        ...labelEdge,
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",
      };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <Button
          size="xs"
          variant="outlined"
          leftSection={<ChevronLeft size={14} />}
          onClick={onBack}
        >
          Back
        </Button>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={title}
        >
          {title}
        </div>
        <div
          role="group"
          aria-label="Zoom"
          style={{ display: "flex", alignItems: "center", gap: 4 }}
        >
          <Button
            size="xs"
            variant="subtle"
            selected={"mode" in zoom && zoom.mode === "fit"}
            disabled={!measured}
            onClick={() => setZoom({ mode: "fit" })}
            // The hint's text would otherwise join the button's name.
            aria-label="Fit label"
            rightSection={<ZoomHint message={FIT_LABEL_HINT} />}
          >
            Fit label
          </Button>
          <Button
            size="xs"
            variant="subtle"
            selected={"mode" in zoom && zoom.mode === "actual"}
            disabled={actualScale === undefined}
            onClick={() => setZoom({ mode: "actual" })}
            aria-label="Actual size"
            rightSection={<ZoomHint message={ACTUAL_SIZE_HINT} />}
          >
            Actual size
          </Button>
          <Button
            size="xs"
            variant="subtle"
            aria-label="Zoom out"
            title="Zoom out"
            disabled={!measured || zoomOut === undefined}
            onClick={() => zoomOut && setZoom({ factor: zoomOut })}
          >
            <ZoomOut size={16} />
          </Button>
          {typedPercent === undefined ? (
            <button
              type="button"
              aria-live="polite"
              aria-label={`Zoom ${Math.round(
                factor * 100
              )}%, click to type a zoom`}
              title="Type a zoom"
              disabled={!measured}
              onClick={() => setTypedPercent(String(Math.round(factor * 100)))}
              style={percentStyle}
            >
              {Math.round(factor * 100)}%
            </button>
          ) : (
            <input
              aria-label={`Zoom percent, ${MIN_ZOOM_PERCENT} to ${MAX_ZOOM_PERCENT}`}
              inputMode="numeric"
              autoFocus
              onFocus={(e) => e.currentTarget.select()}
              value={typedPercent}
              // Digits only: no sign, decimal point or percent sign.
              onChange={(e) =>
                setTypedPercent(e.currentTarget.value.replace(/\D/g, ""))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") commitTypedPercent();
                if (e.key === "Escape") setTypedPercent(undefined);
              }}
              onBlur={commitTypedPercent}
              style={{
                ...percentStyle,
                width: 44,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
          )}
          <Button
            size="xs"
            variant="subtle"
            aria-label="Zoom in"
            title="Zoom in"
            disabled={!measured || zoomIn === undefined}
            onClick={() => zoomIn && setZoom({ factor: zoomIn })}
          >
            <ZoomIn size={16} />
          </Button>
        </div>
      </div>
      <div
        ref={stageRef}
        data-testid="label-preview-zoom-stage"
        style={{
          height: stageHeight,
          // The height is the inside of the stage, which the fit math uses.
          // monolith-ui's global border-box would take the border out of it
          // and leave a fitted label 2px too tall, with a scrollbar.
          boxSizing: "content-box",
          overflow: "auto",
          // Reserves the scrollbar's space up front, so the width measured on
          // mount is still the width once a tall label adds a scrollbar.
          scrollbarGutter: "stable",
          borderRadius: 6,
          border: "1px solid var(--border-color, rgba(128,128,128,0.35))",
          background: theme.palette.background.alt,
          color: theme.palette.text.secondary,
          fontSize: 12,
        }}
      >
        {/* Grows with the image so the stage scrolls, and centers it while it's smaller. */}
        <div
          style={{
            minWidth: "100%",
            minHeight: "100%",
            width: "max-content",
            boxSizing: "border-box",
            padding: STAGE_PADDING,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {src ? (
            <img
              src={src}
              alt={alt}
              style={imageStyle}
              onLoad={(e) => {
                const size = sizeOf(e.currentTarget);
                setShown({ src, size });
                if (src === screenSrc) setScreen({ src, size });
              }}
            />
          ) : (
            placeholder
          )}
        </div>
      </div>
      {nav && nav.count > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <Button
            size="xs"
            variant="subtle"
            aria-label="Previous item"
            title="Previous item"
            disabled={nav.index <= 0}
            onClick={() => nav.onChange(nav.index - 1)}
          >
            <ChevronLeft size={16} />
          </Button>
          <div style={{ fontSize: 12 }}>
            {nav.label} ({nav.index + 1} of {nav.count})
          </div>
          <Button
            size="xs"
            variant="subtle"
            aria-label="Next item"
            title="Next item"
            disabled={nav.index >= nav.count - 1}
            onClick={() => nav.onChange(nav.index + 1)}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      )}
    </div>
  );
}
